import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '@/hooks/project/useProjects';
import { showToast } from '@/components/Toast';
import { useProjectsStore, useConversationsStore, useUIStore } from '@/stores';
import { useHashNavigate } from '@/contexts/HashRouterContext';

export interface UseAppHandlersOptions {
  loadProjects: () => Promise<void>;
  refreshConversations: () => Promise<void>;
  selectProject: (projectId: string | null) => void;
  // 🔥 新增：用于在选择 conversation 时清除 shared 状态
  setChatReadOnly?: (readOnly: boolean) => void;
  setSharedConversationRecordId?: (id: number | null) => void;
}

export interface UseAppHandlersReturn {
  // Project handlers
  handleNewProject: () => void;
  handleCreateProject: (project: { id: string; name: string }) => Promise<void>;
  handleProjectSelect: (projectId: string | null) => void;
  handleRenameProject: (projectId: string, newName: string) => Promise<void>;
  handleDeleteProject: (
    projectId: string,
    role: string | undefined
  ) => Promise<void>;
  handleStarProject: (projectId: string) => Promise<void>;
  handleUpdateProject: (project: {
    id: string;
    name: string;
    description: string;
    instructions: string;
  }) => Promise<void>;
  handleBackToGeneralChats: () => void;
  handleBackToProjects: () => void;

  // Navigation handlers
  handleShowPDFList: () => void;
  onShowAgentList: () => void;
  onShowProjectsList: () => void;
  onShowSharedList: () => void;
  onShowGroupChats: () => void;
  handleBackToChat: () => void;

  // Conversation handlers
  handleProjectConversationSelect: (
    conversationId: string,
    projectId: string
  ) => void;
  handleSwitchToChat: (
    conversationId: string,
    projectId: string,
    messageDetails?: {
      content: string;
      isRetry?: boolean;
      options?: {
        usePublic?: boolean;
        allProjects?: boolean;
        thinkType?: 'think' | 'no-think' | 'hard-think';
        clientId?: string | null;
        pendingId?: string; // 🔥 架构简化：移到 options 中，可选
      };
    }
  ) => void;
}

export function useAppHandlers({
  loadProjects,
  refreshConversations: _refreshConversations,
  selectProject,
  setChatReadOnly,
  setSharedConversationRecordId,
}: UseAppHandlersOptions): UseAppHandlersReturn {
  const { t } = useTranslation();
  const { renameProject, deleteProject, toggleStarProject } = useProjects();

  const { currentProjectId } = useProjectsStore();
  const { projectExpand: globalProjectExpand } = useConversationsStore();

  const {
    setCurrentView,
    setShowNewProjectModal,
    clearCheckboxStates,
    setChatsListInitialTab,
    setSelectedNavItem,
  } = useUIStore();

  const navigate = useHashNavigate();

  // Suppress unused variable warnings - these are kept for future use
  void _refreshConversations;

  // ============ Project Handlers ============

  const handleNewProject = useCallback(() => {
    setShowNewProjectModal(true);
  }, [setShowNewProjectModal]);

  const handleCreateProject = useCallback(
    async (project: { id: string; name: string }) => {
      setTimeout(() => {
        if (window.refreshSidebarProjects) {
          window.refreshSidebarProjects();
        }
        loadProjects();
      }, 100);
      navigate({ type: 'project', projectId: project.id });
    },
    [loadProjects, navigate]
  );

  const handleProjectSelect = useCallback(
    (projectId: string | null) => {
      if (projectId) {
        navigate({ type: 'project', projectId });
        // Refresh projects to update lastVisitedAt and badge count
        setTimeout(() => {
          loadProjects();
        }, 500);
      } else {
        navigate({ type: 'chat' });
      }
    },
    [navigate, loadProjects]
  );

  const handleRenameProject = useCallback(
    async (projectId: string, newName: string) => {
      try {
        const res: any = await renameProject(projectId, newName);
        if (window.refreshSidebarProjects) {
          window.refreshSidebarProjects();
        }
        if (res.code === 200) {
          showToast(
            t('toast.projectRenamedSuccessfully', { name: newName }),
            'success'
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        showToast(
          t('toast.failedToRenameProject', { error: errorMessage }),
          'error'
        );
      }
    },
    [renameProject, t]
  );

  const handleDeleteProject = useCallback(
    async (projectId: string, role: string | undefined) => {
      try {
        if (role !== 'owner') {
          showToast(t('toast.noPermissionToDeleteProject'), 'error');
          return;
        }
        const res: any = await deleteProject(projectId);
        if (res.code === 200) {
          if (window.refreshSidebarProjects) {
            window.refreshSidebarProjects();
          }
          if (currentProjectId === projectId) {
            navigate({ type: 'chat' });
          }
          showToast(t('toast.projectDeletedSuccessfully'), 'success');
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        showToast(
          t('toast.failedToDeleteProject', { error: errorMessage }),
          'error'
        );
      }
    },
    [deleteProject, currentProjectId, navigate, t]
  );

  const handleStarProject = useCallback(
    async (projectId: string) => {
      try {
        await toggleStarProject(projectId);
        if (window.refreshSidebarProjects) {
          window.refreshSidebarProjects();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        showToast(
          t('toast.failedToUpdateProject', { error: errorMessage }),
          'error'
        );
      }
    },
    [toggleStarProject, t]
  );

  const handleUpdateProject = useCallback(
    async (_project: {
      id: string;
      name: string;
      description: string;
      instructions: string;
    }) => {
      await loadProjects();
    },
    [loadProjects]
  );

  const handleBackToGeneralChats = useCallback(() => {
    selectProject(null);
    clearCheckboxStates();
  }, [selectProject, clearCheckboxStates]);

  const handleBackToProjects = useCallback(() => {
    navigate({ type: 'projects' });
  }, [navigate]);

  // ============ Navigation Handlers ============

  const handleShowPDFList = useCallback(() => {
    if (currentProjectId) {
      navigate({ type: 'project-pdfs', projectId: currentProjectId });
    } else {
      // Fallback to direct state change if no project context
      setCurrentView('pdf-list');
    }
  }, [currentProjectId, navigate, setCurrentView]);

  const onShowAgentList = useCallback(() => {
    navigate({ type: 'agents' });
  }, [navigate]);

  const onShowProjectsList = useCallback(() => {
    navigate({ type: 'projects' });
  }, [navigate]);

  const onShowSharedList = useCallback(() => {
    navigate({ type: 'shared' });
  }, [navigate]);

  const onShowGroupChats = useCallback(() => {
    setChatsListInitialTab('groupChats');
    setSelectedNavItem('groupChats');
    navigate({ type: 'chats' });
  }, [navigate, setChatsListInitialTab, setSelectedNavItem]);

  const handleBackToChat = useCallback(() => {
    setChatsListInitialTab('chats');
    setSelectedNavItem('chats');
    navigate({ type: 'chats' });
  }, [navigate, setChatsListInitialTab, setSelectedNavItem]);

  // ============ Conversation Handlers ============

  // 🔥 修复：从项目详情页选择 conversation 时，必须清除 shared conversation 状态
  // 否则从 Shared 页面切换过来后，会渲染错误的 conversation
  const handleProjectConversationSelect = useCallback(
    (conversationId: string, _projectId: string) => {
      setChatReadOnly?.(false);
      setSharedConversationRecordId?.(null);
      navigate({ type: 'chat', conversationId });
    },
    [navigate, setChatReadOnly, setSharedConversationRecordId]
  );

  const handleSwitchToChat = useCallback(
    (
      conversationId: string,
      projectId: string,
      messageDetails?: {
        content: string;
        isRetry?: boolean;
        options?: {
          usePublic?: boolean;
          allProjects?: boolean;
          thinkType?: 'think' | 'no-think' | 'hard-think';
          clientId?: string | null;
          pendingId?: string; // 🔥 架构简化：移到 options 中，可选
        };
      }
    ) => {
      globalProjectExpand(projectId);
      // 先用 conversationId 导航，确保 applyRoute 正确设置 currentConversationId 以渲染对话页面
      navigate({ type: 'chat', conversationId });
      // temp- 开头的临时 ID 不应出现在 URL 中
      // 使用 setTimeout 确保 hashchange 事件已处理完毕（applyRoute 已用 temp ID 正确设置状态），再替换 URL
      if (conversationId.startsWith('temp-')) {
        setTimeout(() => {
          if (window.location.hash.includes('temp-')) {
            window.history.replaceState(null, '', '#/chat');
          }
        }, 0);
      }

      if (messageDetails) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.dispatchEvent(
              new CustomEvent('projectDetailMessage', {
                detail: {
                  conversationId,
                  projectId,
                  ...messageDetails,
                },
              })
            );
          });
        });
      }
    },
    [globalProjectExpand, navigate]
  );

  return {
    // Project handlers
    handleNewProject,
    handleCreateProject,
    handleProjectSelect,
    handleRenameProject,
    handleDeleteProject,
    handleStarProject,
    handleUpdateProject,
    handleBackToGeneralChats,
    handleBackToProjects,

    // Navigation handlers
    handleShowPDFList,
    onShowAgentList,
    onShowProjectsList,
    onShowSharedList,
    onShowGroupChats,
    handleBackToChat,

    // Conversation handlers
    handleProjectConversationSelect,
    handleSwitchToChat,
  };
}
